import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ColumnSelector from "@/components/ColumnSelector";
import ResizeHandle from "@/components/ResizeHandle";
import useTableConfig from "@/hooks/useTableConfig";
import SearchableFilter from "@/components/SearchableFilter";
import { Plus, ArrowLeft, CheckSquare, Trash2, Pencil, Search, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { MarkdownRender, MarkdownEditor } from "@/components/MarkdownEditor";
import { useConfirm } from "@/components/ConfirmDialog";
import { SortableRow, DragHandle, useDragReorder } from "@/components/DragTable";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";

const STATUS_LABELS = { not_picked: "Not Picked", do: "To Do", to_delegate: "To Delegate", waiting_on: "Waiting On", in_review: "In Review", done: "Done" };
const STATUS_COLORS = {
  do: "bg-[#D3E5EF] text-[#183B56] border-transparent",
 
  waiting_on: "bg-[#FFE2DD] text-[#6E3630] border-transparent",
  in_review: "bg-[#FDECC8] text-[#6B4C13] border-transparent",
  done: "bg-[#DBEDDB] text-[#1E4620] border-transparent",
};
const PRIORITY_COLORS = {
  p0: "bg-[#FFE2DD] text-[#6E3630]", p1: "bg-[#FDECC8] text-[#6B4C13]",
  p2: "bg-[#F5E0C3] text-[#5B3F14]", p3: "bg-[#D3E5EF] text-[#183B56]", p4: "bg-[#E8E8E8] text-[#555]",
};
const PRIORITY_ORDER = { p0: 0, p1: 1, p2: 2, p3: 3, p4: 4 };

const TASK_COLUMNS = [
  { key: "task", label: "Task", locked: true },
  { key: "priority", label: "Priority" },
  { key: "assigned", label: "Assigned" },
  { key: "status", label: "Status" },
  { key: "due", label: "Due" },
];

export default function ProjectPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState("");
  const [people, setPeople] = useState([]);
  const { getFilter, setFilter: pf } = useTableConfig("project_tasks", []);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilterL] = useState(() => getFilter("status"));
  const [assignedFilter, setAssignedFilterL] = useState(() => getFilter("assigned"));
  const [sortByPriority, setSortByPriority] = useState(true);
  const [hideCompleted, setHideCompletedL] = useState(() => { const v = getFilter("hide"); return v.length ? v[0] === "true" : true; });
  const setStatusFilter = (v) => { setStatusFilterL(v); pf("status", v); };
  const setAssignedFilter = (v) => { setAssignedFilterL(v); pf("assigned", v); };
  const setHideCompleted = (v) => { setHideCompletedL(v); pf("hide", [String(v)]); };
  const [personPicker, setPersonPicker] = useState(null); // {taskId, status, step?, person?, reminderDate?}
  const { visible: visibleCols, setVisible: setVisibleCols, show, getWidth, setWidth } = useTableConfig("project_tasks", TASK_COLUMNS);

  const fetchData = useCallback(async () => {
    try {
      const [projRes, tasksRes, notesRes, peopleRes] = await Promise.all([
        API.get(`/projects/${projectId}`),
        API.get(`/projects/${projectId}/tasks`),
        API.get(`/projects/${projectId}/notes`),
        API.get("/people"),
      ]);
      setProject(projRes.data);
      setTasks(tasksRes.data);
      setNotes(notesRes.data);
      setPeople(peopleRes.data);
    } catch {
      toast.error("Failed to load project");
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateTask = async () => {
    if (!title.trim()) return;
    try {
      await API.post("/tasks", { title, description, project_id: projectId, goal_id: project.goal_id });
      toast.success("Task created");
      setTitle(""); setDescription(""); setShowCreate(false);
      fetchData();
    } catch { toast.error("Failed to create task"); }
  };

  const changeProjectStatus = async (newStatus) => {
    if (newStatus === "completed") {
      if (!(await confirm("Mark Project Complete", "All tasks under this project will also be marked as done."))) return;
    }
    await API.patch("/projects/" + projectId, { status: newStatus });
    fetchData();
  };

  const handleDeleteProject = async () => {
    if (!(await confirm("Delete Project", "This will permanently delete this project and all its tasks."))) return;
    await API.delete(`/projects/${projectId}`);
    toast.success("Project deleted");
    navigate(-1);
  };

  const patchProject = async (updates) => {
    await API.patch(`/projects/${projectId}`, updates);
    fetchData();
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    await API.post(`/projects/${projectId}/notes`, { content: newNote });
    setNewNote("");
    const r = await API.get(`/projects/${projectId}/notes`);
    setNotes(r.data);
  };
  const deleteNote = async (id) => { await API.delete(`/project-notes/${id}`); const r = await API.get(`/projects/${projectId}/notes`); setNotes(r.data); };
  const startEditNote = (n) => { setEditingNote(n.note_id); setEditNoteContent(n.content); };
  const saveEditNote = async () => { if (!editNoteContent.trim()) return; await API.put(`/project-notes/${editingNote}`, { content: editNoteContent }); setEditingNote(null); const r = await API.get(`/projects/${projectId}/notes`); setNotes(r.data); };

  const { sensors: taskSensors, handleDragEnd: onTaskDragEnd } = useDragReorder(tasks, (reordered) => setTasks(reordered), (reordered) => {
    API.put("/reorder/tasks", { ids: reordered.map(t => t.task_id) }).catch(() => {});
  });

  if (!project) return <div className="text-[#787774]" data-testid="project-loading">Loading...</div>;

  const changeStatus = async (tid, s, aTo) => { const u = { status: s }; if (aTo) u.assigned_to = aTo; await API.put("/tasks/" + tid, u); fetchData(); };
  const changePriority = async (tid, p) => { await API.put("/tasks/" + tid, { priority: p }); fetchData(); };
  const changeAssigned = async (tid, pid) => { await API.put("/tasks/" + tid, { assigned_to: pid }); fetchData(); };
  const handleStatusChange = (tid, s) => { if (s === "waiting_on" || s === "to_delegate") setPersonPicker({ taskId: tid, status: s }); else changeStatus(tid, s); };
  const confirmPersonPick = (pid) => {
    if (!personPicker) return;
    if (personPicker.status === "waiting_on") { setPersonPicker({...personPicker, step: 2, person: pid, reminderDate: ""}); }
    else { changeStatus(personPicker.taskId, personPicker.status, pid); setPersonPicker(null); }
  };
  const confirmWaitingOn = () => {
    if (!personPicker) return;
    const updates = { status: "waiting_on", assigned_to: personPicker.person };
    if (personPicker.reminderDate) { updates.reminder_enabled = true; updates.reminder_date = new Date(personPicker.reminderDate).toISOString(); }
    API.put("/tasks/" + personPicker.taskId, updates).then(() => fetchData());
    setPersonPicker(null);
  };
  const toggleDone = async (tid, cur) => { await API.put("/tasks/" + tid, { status: cur === "done" ? "not_picked" : "done" }); fetchData(); };

  const statusItems = Object.entries(STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }));
  const assignedItems = [...new Map(tasks.filter(t => t.assigned_to).map(t => [t.assigned_to, { value: t.assigned_to, label: people.find(p => p.person_id === t.assigned_to)?.name || t.assigned_to }])).values()];

  let filteredTasks = tasks.filter(t => {
    if (hideCompleted && t.status === "done") return false;
    if (statusFilter.length > 0 && !statusFilter.includes(t.status)) return false;
    if (assignedFilter.length > 0 && !assignedFilter.includes(t.assigned_to)) return false;
    if (search && !t.title?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  if (sortByPriority) filteredTasks = [...filteredTasks].sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 5) - (PRIORITY_ORDER[b.priority] ?? 5));

  const doneCount = tasks.filter(t => t.status === "done").length;
  const progress = tasks.length > 0 ? Math.round((doneCount / tasks.length) * 100) : 0;

  // Group by status
  const statusOrder = ["do", "waiting_on", "in_review", "done"];
  const grouped = statusOrder.reduce((acc, s) => {
    acc[s] = tasks.filter(t => t.status === s);
    return acc;
  }, {});

  return (
    <div data-testid="project-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-[13px] text-[#6B6B6B] mb-4">
        <button onClick={() => navigate(-1)} className="hover:text-[#37352F] transition-colors"><ArrowLeft className="w-3.5 h-3.5 inline mr-1" />Back</button>
        <span className="text-[#B8B6B0]">/</span>
        {project.goal_id && project.goal_title ? (
          <button onClick={() => navigate(`/goals/${project.goal_id}`)} className="hover:text-[#2383E2] hover:underline">{project.goal_title}</button>
        ) : <span className="text-[#B8B6B0]">No Goal</span>}
        <span className="text-[#B8B6B0]">/</span>
        <span className="text-[#37352F] font-medium truncate max-w-[200px]">{project.title}</span>
      </div>

      {/* Project header */}
      <div className="border border-[#E0E0DE] bg-[#F7F6F3]/40 rounded-sm p-6 mb-8">
        <div className="flex items-start justify-between">
          <div>
            <textarea ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }} value={project.title} onChange={e => setProject({...project, title: e.target.value})} onBlur={() => patchProject({ title: project.title })}
              className="w-full text-[28px] font-bold text-[#37352F] placeholder:text-[#A3A3A0] bg-transparent border-none outline-none leading-tight resize-none overflow-hidden"
              placeholder="Untitled" rows={1} onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} data-testid="project-title" />
            <textarea ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }} value={project.description || ""} onChange={e => setProject({...project, description: e.target.value})} onBlur={() => patchProject({ description: project.description })}
              className="w-full text-[15px] text-[#37352F] placeholder:text-[#A3A3A0] bg-transparent border-none outline-none resize-none leading-relaxed min-h-[20px] mt-1"
              placeholder="Add a description..." rows={1} onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} />
          </div>
          <div className="flex gap-1 ml-4">

          {/* Due date inline */}
          </div>
        </div>
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[#E0E0DE] text-[13px]">
          <span className="text-[#6B6B6B]">Start</span>
          <input type="date" value={project.start_date || ""} onChange={e => patchProject({ start_date: e.target.value || null })}
            className="bg-transparent border-none outline-none text-[#37352F] hover:bg-[#E8E8E6] rounded-[4px] px-2 py-0.5 text-[13px]" />
          {project.start_date && <button onClick={() => patchProject({ start_date: null })} className="text-[11px] text-[#6B6B6B] hover:text-[#EB5757]">&times;</button>}
          <span className="text-[#6B6B6B] ml-4">Due</span>
          <input type="date" value={project.due_date || ""} onChange={e => patchProject({ due_date: e.target.value || null })}
            className="bg-transparent border-none outline-none text-[#37352F] hover:bg-[#E8E8E6] rounded-[4px] px-2 py-0.5 text-[13px]" />
          {project.due_date && <button onClick={() => patchProject({ due_date: null })} className="text-[11px] text-[#6B6B6B] hover:text-[#EB5757]">&times;</button>}
          <span className="text-[#6B6B6B] ml-4">Priority</span>
          <select value={project.priority || ""} onChange={e => patchProject({ priority: e.target.value || null })}
            className="bg-transparent border-none outline-none text-[#37352F] hover:bg-[#E8E8E6] rounded-[4px] px-2 py-0.5 text-[13px] cursor-pointer">
            <option value="">None</option>
            <option value="p0">P0</option><option value="p1">P1</option><option value="p2">P2</option><option value="p3">P3</option><option value="p4">P4</option>
          </select>
        </div>
        <div className="flex items-start gap-3 mt-2 text-[13px]">
          <span className="text-[#6B6B6B] shrink-0 pt-0.5">People</span>
          <div className="flex flex-wrap items-center gap-1">
            {(project.people_involved || []).map(pid => {
              const person = people.find(p => p.person_id === pid);
              return person ? (<span key={pid} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E8E8E6] rounded-[3px] text-[12px] text-[#37352F]">{person.name}<button onClick={() => patchProject({ people_involved: (project.people_involved || []).filter(id => id !== pid) })} className="text-[#A3A3A0] hover:text-[#EB5757]">&times;</button></span>) : null;
            })}
            <select onChange={e => { const v = e.target.value; if (v && !(project.people_involved || []).includes(v)) patchProject({ people_involved: [...(project.people_involved || []), v] }); e.target.value = ""; }}
              className="bg-transparent border-none outline-none text-[12px] text-[#6B6B6B] hover:bg-[#E8E8E6] rounded-[4px] px-1.5 py-0.5 cursor-pointer">
              <option value="">+ Add</option>
              {people.filter(p => !(project.people_involved || []).includes(p.person_id)).map(p => <option key={p.person_id} value={p.person_id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-start justify-between mt-4 pt-3 border-t border-[#E0E0DE]">
          <div className="flex items-center gap-4 text-sm text-[#787774]">
            <div className="flex items-center gap-1.5">
              <CheckSquare className="w-4 h-4" strokeWidth={1.5} />
              <span className="font-medium text-[#37352F]">{doneCount}</span>/{tasks.length} tasks done
            </div>
            {tasks.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-24 h-1.5 bg-[#E0E0DE] rounded-full"><div className="h-full bg-[#2383E2] rounded-full" style={{ width: `${progress}%` }} /></div>
                <span className="text-xs text-[#2383E2] font-medium">{progress}%</span>
              </div>
            )}
          </div>
          <div className="flex gap-1">
            <DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline" size="sm" className={"text-[12px] h-7 " + (project.status === "completed" ? "bg-[#DBEDDB] text-[#1E4620] border-[#DBEDDB]" : project.status === "in_progress" ? "bg-[#D3E5EF] text-[#183B56] border-[#D3E5EF]" : "border-[#E0E0DE] text-[#6B6B6B]")} data-testid="toggle-project-complete">{project.status === "completed" ? "Completed" : project.status === "in_progress" ? "In Progress" : "Active"}</Button></DropdownMenuTrigger><DropdownMenuContent className="bg-white border-[#E0E0DE] shadow-sm"><DropdownMenuItem onClick={() => changeProjectStatus("active")} className="text-[13px] cursor-pointer">Active</DropdownMenuItem><DropdownMenuItem onClick={() => changeProjectStatus("in_progress")} className="text-[13px] cursor-pointer">In Progress</DropdownMenuItem><DropdownMenuItem onClick={() => changeProjectStatus("completed")} className="text-[13px] cursor-pointer">Completed</DropdownMenuItem></DropdownMenuContent></DropdownMenu>
            <Button onClick={() => setShowCreate(true)} className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white" size="sm" data-testid="create-task-button">
              <Plus className="w-4 h-4 mr-1" /> New Task
            </Button>
            <Button onClick={handleDeleteProject} variant="ghost" size="icon" className="text-[#EB5757]" data-testid="delete-project-button">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Task filters */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 max-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A3A3A0]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks..." className="w-full pl-8 pr-3 py-[5px] text-[13px] border border-[#E0E0DE] rounded-[4px] bg-transparent outline-none focus:border-[#2383E2]/50 text-[#37352F] placeholder:text-[#A3A3A0]" />
        </div>
        <button onClick={() => setHideCompleted(!hideCompleted)} className={"text-[12px] px-2 py-1 border rounded-[4px] transition-colors " + (hideCompleted ? "border-[#2383E2] text-[#2383E2] bg-[#2383E2]/5" : "border-[#E0E0DE] text-[#6B6B6B]")}>{hideCompleted ? "Show done" : "Hide done"}</button>
        <SearchableFilter label="Status" items={statusItems} values={statusFilter} onChange={setStatusFilter} />
        <SearchableFilter label="Assigned" items={assignedItems} values={assignedFilter} onChange={setAssignedFilter} />
        <button onClick={() => setSortByPriority(!sortByPriority)} className={"flex items-center gap-1 px-2 py-[5px] text-[12px] border rounded-[4px] transition-colors " + (sortByPriority ? "border-[#2383E2] text-[#2383E2] bg-[#2383E2]/5" : "border-[#E0E0DE] text-[#6B6B6B] hover:bg-[#E8E8E6]")}><ArrowUpDown className="w-3 h-3" /> Priority</button>
        <ColumnSelector columns={TASK_COLUMNS} visible={visibleCols} onChange={setVisibleCols} />
      </div>

      {/* Tasks table */}
      {filteredTasks.length === 0 && tasks.length === 0 ? (
        <div className="text-center py-16 border border-[#E0E0DE] border-dashed rounded-sm">
          <CheckSquare className="w-10 h-10 text-[#E0E0DE] mx-auto mb-3" strokeWidth={1} />
          <p className="text-[#787774] italic">No tasks yet</p>
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-8 text-[#6B6B6B] text-[14px]">No tasks match filters</div>
      ) : (
        <div className="border border-[#E0E0DE] rounded-[4px] overflow-auto">
          <Table>
            <colgroup>
              <col style={{ width: 60 }} />
              <col style={getWidth("task") ? { width: getWidth("task") } : { width: 300 }} />
              {show("priority") && <col style={getWidth("priority") ? { width: getWidth("priority") } : { width: 80 }} />}
              {show("assigned") && <col style={getWidth("assigned") ? { width: getWidth("assigned") } : { width: 110 }} />}
              {show("status") && <col style={getWidth("status") ? { width: getWidth("status") } : { width: 110 }} />}
              {show("due") && <col style={getWidth("due") ? { width: getWidth("due") } : { width: 100 }} />}
              <col style={{width: 36}} />
            </colgroup>
            <TableHeader>
              <TableRow className="bg-[#F7F6F3] hover:bg-[#F7F6F3] border-b border-[#E0E0DE]">
                <TableHead className="w-10 text-[11px] font-medium text-[#6B6B6B]">Done</TableHead>
                <TableHead className="text-[11px] font-medium text-[#6B6B6B] relative">Task<ResizeHandle colKey="task" onResize={setWidth} /></TableHead>
                {show("priority") && <TableHead className="text-[11px] font-medium text-[#6B6B6B] relative">Priority<ResizeHandle colKey="priority" onResize={setWidth} /></TableHead>}
                {show("assigned") && <TableHead className="text-[11px] font-medium text-[#6B6B6B] relative">Assigned<ResizeHandle colKey="assigned" onResize={setWidth} /></TableHead>}
                {show("status") && <TableHead className="text-[11px] font-medium text-[#6B6B6B] relative">Status<ResizeHandle colKey="status" onResize={setWidth} /></TableHead>}
                {show("due") && <TableHead className="text-[11px] font-medium text-[#6B6B6B] relative">Due<ResizeHandle colKey="due" onResize={setWidth} /></TableHead>}
              </TableRow>
                <TableHead className="w-8"></TableHead>
            </TableHeader>
            <TableBody>
              <DndContext sensors={taskSensors} collisionDetection={closestCenter} onDragEnd={onTaskDragEnd}>
                <SortableContext items={filteredTasks.map(t => t.task_id)} strategy={verticalListSortingStrategy}>
              {filteredTasks.map(task => (
                <SortableRow key={task.task_id} id={task.task_id}>{({ dragProps }) => (<>
                  <TableCell><DragHandle dragProps={dragProps} /><Checkbox checked={task.status === "done"} onCheckedChange={() => toggleDone(task.task_id, task.status)} className="h-[18px] w-[18px] rounded-[3px] border-[#B8B6B0] data-[state=checked]:bg-[#2383E2] data-[state=checked]:border-[#2383E2] cursor-pointer" /></TableCell>
                  <TableCell className={"text-[13px] font-medium cursor-pointer hover:underline " + (task.status === "done" ? "line-through text-[#A3A3A0]" : "text-[#37352F]")} onClick={() => navigate("/tasks/" + task.task_id)}>{task.title}</TableCell>
                  {show("priority") && <TableCell onClick={e => e.stopPropagation()}><DropdownMenu><DropdownMenuTrigger asChild><button className="cursor-pointer">{task.priority ? <span className={"text-[11px] px-1.5 py-0.5 rounded-[3px] font-medium " + (PRIORITY_COLORS[task.priority] || "")}>{task.priority.toUpperCase()}</span> : <span className="text-[#B8B6B0] text-[12px] hover:text-[#6B6B6B]">Set</span>}</button></DropdownMenuTrigger><DropdownMenuContent className="bg-white border-[#E0E0DE] shadow-sm"><DropdownMenuItem onClick={() => changePriority(task.task_id, null)} className="text-[13px] cursor-pointer">None</DropdownMenuItem>{["p0","p1","p2","p3","p4"].map(p => <DropdownMenuItem key={p} onClick={() => changePriority(task.task_id, p)} className="text-[13px] cursor-pointer">{p.toUpperCase()}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu></TableCell>}
                  {show("assigned") && <TableCell onClick={e => e.stopPropagation()}><DropdownMenu><DropdownMenuTrigger asChild><button className="cursor-pointer text-[13px]">{people.find(p => p.person_id === task.assigned_to)?.name || <span className="text-[#B8B6B0] hover:text-[#6B6B6B]">Set</span>}</button></DropdownMenuTrigger><DropdownMenuContent className="bg-white border-[#E0E0DE] shadow-sm"><DropdownMenuItem onClick={() => changeAssigned(task.task_id, null)} className="text-[13px] cursor-pointer">None</DropdownMenuItem>{people.map(p => <DropdownMenuItem key={p.person_id} onClick={() => changeAssigned(task.task_id, p.person_id)} className="text-[13px] cursor-pointer">{p.name}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu></TableCell>}
                  {show("status") && <TableCell onClick={e => e.stopPropagation()}><DropdownMenu><DropdownMenuTrigger asChild><button><Badge variant="outline" className={"text-[11px] cursor-pointer border-0 font-medium rounded-[3px] " + (STATUS_COLORS[task.status] || "")}>{STATUS_LABELS[task.status]}</Badge></button></DropdownMenuTrigger><DropdownMenuContent className="bg-white border-[#E0E0DE] shadow-sm">{Object.entries(STATUS_LABELS).map(([v, l]) => <DropdownMenuItem key={v} onClick={() => handleStatusChange(task.task_id, v)} className="text-[13px] cursor-pointer">{l}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu></TableCell>}
                  {show("due") && <TableCell onClick={e=>e.stopPropagation()} className="text-[12px]"><input type="date" value={task.due_date||""} onChange={e=>{API.put("/tasks/"+task.task_id,{due_date:e.target.value||null}).then(()=>fetchData())}} className="bg-transparent border-none outline-none text-[#37352F] hover:bg-[#E8E8E6] rounded-[4px] px-1 py-0.5 text-[12px] w-[100px] cursor-pointer" /></TableCell>}
                  <TableCell onClick={e=>e.stopPropagation()} className="text-center"><button onClick={async ()=>{if(!(await confirm("Delete Task","This will permanently delete this task.")))return;await API.delete("/tasks/"+task.task_id);fetchData();}} className="text-[#B8B6B0] hover:text-[#EB5757] p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5"/></button></TableCell>
                </>)}</SortableRow>
              ))}
                </SortableContext>
              </DndContext>
            </TableBody>
          </Table>
        </div>
      )}

      {/* Person picker for delegate/waiting_on */}
      <Dialog open={!!personPicker} onOpenChange={v => { if (!v) setPersonPicker(null); }}>
        <DialogContent className="bg-white border-[#E0E0DE] shadow-sm max-w-sm">
          <DialogHeader><DialogTitle className="text-[15px] font-semibold text-[#37352F]">{personPicker?.step===2?"When to follow up?":personPicker?.status==="waiting_on"?"Waiting on who?":"Delegate to who?"}</DialogTitle></DialogHeader>
          {personPicker?.step!==2 ? (
            <div className="space-y-1">{people.map(p => (<button key={p.person_id} onClick={() => confirmPersonPick(p.person_id)} className="w-full text-left px-3 py-2 text-[14px] text-[#37352F] rounded-[4px] hover:bg-[#F7F6F3]">{p.name}</button>))}</div>
          ) : (
            <div className="space-y-3">
              <p className="text-[13px] text-[#6B6B6B]">Task will be marked done. A follow-up task will be created.</p>
              <div><label className="text-[12px] text-[#6B6B6B] block mb-1">Follow-up date</label><input type="date" value={personPicker.reminderDate||""} onChange={e=>setPersonPicker({...personPicker,reminderDate:e.target.value})} className="w-full text-[14px] border border-[#E0E0DE] rounded-[4px] px-3 py-2 outline-none focus:ring-1 focus:ring-[#2383E2]/30"/></div>
              <div className="flex gap-2 justify-end"><button onClick={()=>setPersonPicker({...personPicker,step:1})} className="text-[13px] text-[#6B6B6B] px-3 py-1.5">Back</button><button onClick={confirmWaitingOn} className="text-[13px] bg-[#2383E2] hover:bg-[#1B6EC2] text-white px-4 py-1.5 rounded-[4px]">{personPicker.reminderDate?"Delegate & Schedule":"Delegate Without Date"}</button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Notes */}
      <div className="mt-8">
        <h3 className="text-[14px] font-semibold text-[#37352F] mb-3">Notes</h3>
        <div className="mb-4" data-testid="project-note-input">
          <MarkdownEditor value={newNote} onChange={setNewNote} onSubmit={addNote} />
        </div>
        <div className="space-y-2">
          {notes.map(note => (
            <div key={note.note_id} className="group rounded-[6px] bg-[#F7F6F3] p-3" data-testid={`pnote-${note.note_id}`}>
              {editingNote === note.note_id ? (
                <div>
                  <textarea value={editNoteContent} onChange={e => setEditNoteContent(e.target.value)}
                    className="w-full text-[14px] bg-white border border-[#E0E0DE] rounded-[4px] outline-none resize-none p-2 text-[#37352F] focus:ring-1 focus:ring-[#2383E2]/30" rows={3} autoFocus />
                  <div className="flex gap-2 mt-2"><button onClick={saveEditNote} className="text-[12px] font-medium text-[#2383E2]">Save</button><button onClick={() => setEditingNote(null)} className="text-[12px] text-[#6B6B6B]">Cancel</button></div>
                </div>
              ) : (
                <>
                  <MarkdownRender content={note.content} />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[11px] text-[#6B6B6B]">{note.author_name} &middot; {new Date(note.created_at).toLocaleString()}</p>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => startEditNote(note)} className="text-[#6B6B6B] hover:text-[#37352F] p-0.5"><Pencil className="w-3 h-3" /></button>
                      <button onClick={() => deleteNote(note.note_id)} className="text-[#6B6B6B] hover:text-[#EB5757] p-0.5"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#FFFFFF] border-[#E0E0DE]">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#37352F]">New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[12px] text-[#6B6B6B]">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-transparent border-[#E0E0DE]" data-testid="task-title-input" />
            </div>
            <div>
              <Label className="text-[12px] text-[#6B6B6B]">Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 bg-transparent border-[#E0E0DE] resize-none" data-testid="task-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCreateTask} className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white" data-testid="submit-task-button">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
